import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useLanguage, useSEO } from "../context/LanguageContext";
import { PageHero, SectionHeading, Reveal, CTAButton, Overline } from "../components/Kinetic";

const DATA_IMG =
  "https://images.unsplash.com/photo-1644088379091-d574269d422f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGRhdGElMjB2aXN1YWxpemF0aW9ufGVufDB8fHx8MTc4ODEwNjI5Mnww&ixlib=rb-4.1.0&q=85";

const RecherchePage = () => {
  const { t } = useLanguage();
  useSEO(t("recherche.metaTitle"), t("recherche.metaDesc"));
  const blocks = t("recherche.blocks");

  return (
    <div data-testid="recherche-page">
      <PageHero
        overline={t("recherche.overline")}
        titleLines={t("recherche.titleLines")}
        intro={t("recherche.intro")}
        testid="recherche"
      />

      <section className="py-24 md:py-32">
        <div className="mx-auto max-w-7xl px-6">
          <SectionHeading overline={t("recherche.blocksOverline")} title={t("recherche.blocksTitle")} />
          <div className="mt-16 grid gap-px bg-graphite/10 md:grid-cols-2">
            {blocks.map((b, i) => (
              <Reveal key={b.num} delay={i * 0.08} className="group bg-bone p-8 md:p-12 transition-colors duration-300 hover:bg-science">
                <p className="font-mono text-sm text-science transition-colors duration-300 group-hover:text-white/60">{b.num}</p>
                <h3 className="mt-6 font-display text-2xl md:text-3xl font-light tracking-tight text-graphite transition-colors duration-300 group-hover:text-white">
                  {b.title}
                </h3>
                <p className="mt-4 text-sm md:text-base leading-relaxed text-graphite-soft transition-colors duration-300 group-hover:text-white/70">
                  {b.text}
                </p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-graphite/10 bg-white py-24 md:py-32">
        <div className="mx-auto max-w-7xl px-6">
          <SectionHeading overline={t("researchAxes.overline")} title={t("researchAxes.title")} text={t("researchAxes.note")} />
          <div className="mt-16 grid gap-px bg-graphite/10 md:grid-cols-3">
            {t("researchAxes.items").map((a, i) => (
              <Reveal key={a.num} delay={i * 0.1} className="group bg-white p-8 md:p-10 transition-colors duration-300 hover:bg-forest">
                <p className="font-mono text-sm text-forest transition-colors duration-300 group-hover:text-white/60">{a.num}</p>
                <h3 className="mt-6 font-display text-2xl md:text-3xl font-light tracking-tight text-graphite transition-colors duration-300 group-hover:text-white">
                  {a.title}
                </h3>
                <p className="mt-4 text-sm leading-relaxed text-graphite-soft transition-colors duration-300 group-hover:text-white/70">
                  {a.text}
                </p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-forest py-24 text-white md:py-32">
        <div className="mx-auto max-w-7xl px-6">
          <SectionHeading overline={t("veille.overline")} title={t("veille.title")} text={t("veille.text")} light />
          <div className="mt-12 flex flex-wrap gap-3">
            {t("veille.themes").map((theme, i) => (
              <Reveal key={theme} delay={i * 0.03} y={10}>
                <span className="inline-block border border-white/20 px-4 py-2 font-mono text-[11px] uppercase tracking-[0.15em] text-white/70 transition-colors duration-200 hover:border-white hover:text-white">
                  {theme}
                </span>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <div className="mt-10">
              <CTAButton to="/actualites" variant="light" testid="recherche-cta-veille">
                {t("veille.cta")}
              </CTAButton>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-24 md:py-32">
        <div className="mx-auto max-w-7xl px-6">
          <SectionHeading overline={t("researchNav.overline")} title={t("researchNav.title")} />
          <div className="mt-14">
            {t("researchNav.items").map((item, i) => (
              <Reveal key={item.to} delay={i * 0.05} y={14}>
                <Link
                  to={item.to}
                  className="group flex items-center justify-between border-b border-graphite/10 py-6 transition-colors duration-200 hover:border-forest/50"
                  data-testid={`research-nav-${i}`}
                >
                  <span className="font-display text-xl md:text-3xl font-light tracking-tight text-graphite transition-colors duration-200 group-hover:text-forest">
                    {item.label}
                  </span>
                  <ArrowUpRight className="h-5 w-5 text-graphite/30 transition-all duration-300 group-hover:translate-x-1 group-hover:-translate-y-1 group-hover:text-forest" />
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden bg-graphite py-24 text-white md:py-32">
        <motion.img
          src={DATA_IMG}
          alt=""
          loading="lazy"
          initial={{ scale: 1.1, opacity: 0 }}
          whileInView={{ scale: 1, opacity: 0.25 }}
          viewport={{ once: true }}
          transition={{ duration: 1.4 }}
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="relative mx-auto max-w-7xl px-6">
          <Reveal y={16}>
            <Overline light>Green Horizon Project</Overline>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-5 max-w-3xl font-display text-3xl md:text-5xl font-light tracking-tight leading-tight">
              {t("recherche.honestyTitle")}
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-6 max-w-2xl text-base md:text-lg leading-relaxed text-white/70">
              {t("recherche.honestyText")}
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="mt-10">
              <CTAButton to="/programme" variant="light" testid="recherche-cta-programme">
                {t("recherche.cta")}
              </CTAButton>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default RecherchePage;
